// UserManagement.jsx — the unified "Team & Providers" section.
//
// Owner directive 2026-08-15: Users & Roles and Providers were two separate
// sections for what is really ONE thing — a staff member. This screen merges
// them: every person shows their login account (role, status, password,
// notifications) AND their provider profile (calendar color, services, weekly
// hours) in one place, linked by providers.user_id → profiles.id.
//
// Sync model (deliberately simple):
//   • profiles = the account (login, role, permissions).
//   • providers = the bookable calendar identity (color, services, hours).
//   • One optional link between them. Creating a user can create both at once;
//     deleting a user NEVER deletes the provider row (appointment history
//     lives there) — it just unlinks it.
//   • Non-admin staff who only have the old 'providers' permission get the
//     classic ProvidersManager view — nothing they could do before is lost.
import { useState, useEffect } from "react";
import { supabase } from "./supabase";
import { useAuth } from "./AuthContext";
import PayrollSettings from "./PayrollSettings";
import { ProvidersManager, ProviderFormModal, ProviderHoursModal, PROVIDER_COLORS } from "./ProvidersManager";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldL:"#d4a853", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const ROLES = [
  { id: "admin", label: "Admin", desc: "Full access to everything", color: C.gold },
  { id: "director", label: "Director", desc: "Manages operations & business", color: C.purple },
  { id: "provider", label: "Provider (MD/NP)", desc: "Medical staff with full clinical", color: C.blue },
  { id: "nurse", label: "Nurse", desc: "Clinical staff", color: C.green },
  { id: "esthetician", label: "Esthetician", desc: "Facials, peels, skincare services", color: "#ec4899" },
  { id: "frontdesk", label: "Front Desk", desc: "Reception & checkout", color: C.red },
];

// Account role → provider-profile role (different taxonomies; best-effort map).
const ACCOUNT_TO_PROVIDER_ROLE = { provider: "np", nurse: "rn", esthetician: "esthetician" };
// Clinical account roles default the "bookable provider" toggle ON when creating.
const BOOKABLE_BY_DEFAULT = new Set(["provider", "nurse", "esthetician"]);

const MODULE_LABELS = {
  // Top-level modules
  pay: "💵 Elyon Pay",
  marketing: "📣 Marketing",
  aiagent: "🎯 AI Agent",
  // Operations
  dashboard: "📊 Dashboard",
  frontdesk: "🏪 Front Desk",
  appointments: "📅 Appointments",
  checkout: "💳 Checkout/POS",
  takepay: "💵 Take Payment",
  messages: "💬 Messages",
  // Clinical
  clients: "👥 Clients & EMR",
  gfe: "⚕️ GFE Management",
  providers: "👨‍⚕️ Providers",
  consents: "📋 Consents",
  // Business
  memberships: "⭐ Memberships",
  services: "✨ Services",
  packages: "📦 Packages",
  inventory: "📦 Inventory",
  payroll: "💰 Payroll",
  // Growth
  bookinglink: "🔗 Booking Link",
  sales: "💸 Sales",
  analytics: "📈 Analytics",
  clientinsights: "🔍 Client Insights",
  winback: "🔁 Win-Back Engine",
  mission: "🎯 Today's Mission",
  leadengine: "🔥 Lead Engine",
  pipeline: "📊 Pipeline Board",
  treatments: "📊 Treatment Analytics",
  // Platform
  users: "🔐 Team & Providers",
  ai: "🤖 AI Assistant",
  bottraining: "🎓 Bot Training",
  automations: "⚙️ Reminders & Automations",
};

const inp = { width:"100%",padding:"10px 13px",background:C.light,border:`1px solid ${C.border}`,borderRadius:8,fontSize:13,color:C.text,fontFamily:F,outline:"none",boxSizing:"border-box" };
const btn = (bg, color, border) => ({ padding:"6px 12px",fontSize:11,fontWeight:600,background:bg,color,border:border||"none",borderRadius:7,cursor:"pointer",fontFamily:F });

export default function UserManagement() {
  const { profile: currentProfile, permissions: rolePerms, ALL_MODULES, refreshPermissions, hasAccess } = useAuth();
  const [users, setUsers] = useState([]);
  const [providers, setProviders] = useState([]);
  const [services, setServices] = useState([]);
  const [providerServices, setProviderServices] = useState({});
  const [tab, setTab] = useState("team");
  const [showCreate, setShowCreate] = useState(false);
  const [showPwd, setShowPwd] = useState(null);
  const [showNotif, setShowNotif] = useState(null);
  const [notifDraft, setNotifDraft] = useState({ notif_phone: "", notif_prefs: {} });
  const [form, setForm] = useState({ full_name:"", email:"", password:"", role:"frontdesk", bookable:false, color:PROVIDER_COLORS[0] });
  const [newPwd, setNewPwd] = useState("");
  const [loading, setLoading] = useState(false);
  const [msg, setMsg] = useState("");
  const [editPerms, setEditPerms] = useState({});
  const [provForm, setProvForm] = useState(null);   // provider being edited (with _services)
  const [hoursFor, setHoursFor] = useState(null);   // provider id whose hours modal is open

  const isAdmin = currentProfile?.role === "admin";

  useEffect(() => {
    loadAll();
    setEditPerms(rolePerms);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rolePerms]);

  const loadAll = async () => {
    const { data } = await supabase.from("profiles").select("*").order("created_at", { ascending: false });
    setUsers(data || []);
    const { data: provs } = await supabase.from("providers").select("*").order("order_index").order("name");
    setProviders(provs || []);
    const { data: svcs } = await supabase.from("services").select("*").eq("active", true).order("category").order("name");
    setServices(svcs || []);
    const { data: ps } = await supabase.from("provider_services").select("*");
    const map = {};
    (ps || []).forEach(r => { (map[r.provider_id] = map[r.provider_id] || []).push(r.service_id); });
    setProviderServices(map);
  };

  const callFunction = async (body) => {
    const { data: { session } } = await supabase.auth.getSession();
    const supabaseUrl = "https://fuoejykgpnfphrvqjuub.supabase.co";
    const response = await fetch(`${supabaseUrl}/functions/v1/create-user`, {
      method: "POST",
      headers: {
        "Authorization": `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });
    return await response.json();
  };

  // ── provider helpers ──────────────────────────────────────────────────
  const providerOf = (userId) => providers.find(p => p.user_id === userId);
  const orphanProviders = providers.filter(p => !p.user_id || !users.some(u => u.id === p.user_id));

  const openProviderEdit = (p) => setProvForm({ ...p, _services: providerServices[p.id] || [] });

  const saveProvider = async () => {
    if (!provForm?.name) { setMsg("Error: Provider name is required"); return; }
    const data = { ...provForm };
    const selectedSvcIds = data._services || [];
    delete data._services;
    delete data.id;
    let providerId = provForm.id;
    if (provForm.id) {
      const { error } = await supabase.from("providers").update(data).eq("id", provForm.id);
      if (error) { setMsg("Error: " + error.message); return; }
    } else {
      const { data: created, error } = await supabase.from("providers").insert([data]).select().single();
      if (error) { setMsg("Error: " + error.message); return; }
      providerId = created?.id;
    }
    if (providerId) {
      await supabase.from("provider_services").delete().eq("provider_id", providerId);
      if (selectedSvcIds.length > 0) {
        await supabase.from("provider_services").insert(selectedSvcIds.map(sid => ({ provider_id: providerId, service_id: sid })));
      }
    }
    setProvForm(null);
    setMsg("Provider settings saved");
    loadAll();
  };

  // Give an existing account a calendar identity (provider row) in one click.
  const makeBookable = async (u) => {
    const row = {
      name: u.full_name || u.email,
      email: u.email || "",
      role: ACCOUNT_TO_PROVIDER_ROLE[u.role] || "provider",
      color: PROVIDER_COLORS[providers.length % PROVIDER_COLORS.length],
      specialties: [], phone: "", bio: "", photo_url: "",
      active: true,
      order_index: providers.length,
      user_id: u.id,
    };
    const { data: created, error } = await supabase.from("providers").insert([row]).select().single();
    if (error) { setMsg("Error: " + error.message); return; }
    setMsg(`${row.name} is now bookable — pick their services below, then set hours.`);
    await loadAll();
    setProvForm({ ...created, _services: [] });
  };

  // ── account actions (unchanged behavior) ──────────────────────────────
  const handleCreate = async () => {
    if (!form.email || !form.password || !form.full_name) { setMsg("Please fill all fields"); return; }
    if (form.password.length < 6) { setMsg("Password must be at least 6 characters"); return; }
    setLoading(true);
    setMsg("");
    const result = await callFunction({ action:"create", full_name: form.full_name, email: form.email.trim().toLowerCase(), password: form.password, role: form.role });
    if (result.error) {
      setLoading(false);
      setMsg("Error: " + result.error);
      return;
    }
    // Account created. If flagged bookable, create the linked provider row too
    // and drop straight into the services picker so setup finishes in one flow.
    let createdProvider = null;
    if (form.bookable && result.user_id) {
      const row = {
        name: form.full_name, email: form.email.trim().toLowerCase(),
        role: ACCOUNT_TO_PROVIDER_ROLE[form.role] || "provider",
        color: form.color || PROVIDER_COLORS[providers.length % PROVIDER_COLORS.length],
        specialties: [], phone: "", bio: "", photo_url: "",
        active: true, order_index: providers.length, user_id: result.user_id,
      };
      const { data: created, error } = await supabase.from("providers").insert([row]).select().single();
      if (error) setMsg("User created, but provider setup failed: " + error.message);
      else createdProvider = created;
    }
    setLoading(false);
    setForm({ full_name:"", email:"", password:"", role:"frontdesk", bookable:false, color:PROVIDER_COLORS[0] });
    setShowCreate(false);
    await loadAll();
    if (createdProvider) {
      setMsg("User created! Now pick their services (then set hours from their row).");
      setProvForm({ ...createdProvider, _services: [] });
    } else {
      setMsg("User created successfully!");
    }
  };

  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user "${userName}"? This cannot be undone.`)) return;
    setLoading(true);
    const result = await callFunction({ action:"delete", user_id: userId });
    if (result.error) {
      setLoading(false);
      setMsg("Error: " + result.error);
      return;
    }
    // Keep the provider row (appointment history points at it) but unlink the
    // deleted account so the row shows under "Providers without login".
    const linked = providers.find(p => p.user_id === userId);
    if (linked) {
      await supabase.from("providers").update({ user_id: null }).eq("id", linked.id);
      setMsg(`User deleted. Provider "${linked.name}" kept (history) — now unlinked.`);
    } else {
      setMsg("User deleted");
    }
    setLoading(false);
    loadAll();
  };

  const handleRoleChange = async (userId, newRole) => {
    await supabase.from("profiles").update({ role: newRole }).eq("id", userId);
    loadAll();
  };

  const handleStatusToggle = async (userId, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    await supabase.from("profiles").update({ status: newStatus }).eq("id", userId);
    // Mirror to the linked provider so a deactivated staffer stops being
    // bookable immediately (and reactivates together).
    const linked = providers.find(p => p.user_id === userId);
    if (linked) await supabase.from("providers").update({ active: newStatus === "active" }).eq("id", linked.id);
    loadAll();
  };

  const openNotifPrefs = (user) => {
    setShowNotif(user);
    setNotifDraft({
      notif_phone: user.notif_phone || "",
      notif_prefs: user.notif_prefs || {
        new_leads: true,
        new_bookings: true,
        complaints: true,
        cancellations: false,
        no_shows: true,
      },
    });
  };

  const saveNotifPrefs = async () => {
    setLoading(true);
    const { error } = await supabase.from("profiles").update({
      notif_phone: notifDraft.notif_phone || null,
      notif_prefs: notifDraft.notif_prefs,
    }).eq("id", showNotif.id);
    setLoading(false);
    if (error) { setMsg("Save failed: " + error.message); return; }
    setShowNotif(null);
    setMsg("Notifications updated for " + (showNotif.full_name || showNotif.email));
    loadAll();
  };

  const handlePasswordChange = async () => {
    if (newPwd.length < 6) { setMsg("Password must be at least 6 characters"); return; }
    setLoading(true);
    const result = await callFunction({ action:"update_password", user_id: showPwd.id, password: newPwd });
    setLoading(false);
    if (result.error) {
      setMsg("Error: " + result.error);
    } else {
      setMsg("Password updated!");
      setShowPwd(null);
      setNewPwd("");
    }
  };

  // ★ FORCE-RESET — handles the "I reset her password but she still can't
  //   log in" case (repairs missing/broken auth records; fixes Bani's case).
  const handleForceReset = async () => {
    if (!showPwd?.email) { setMsg("Missing email on this profile"); return; }
    if (newPwd.length < 6) { setMsg("Password must be at least 6 characters"); return; }
    setLoading(true);
    const result = await callFunction({
      action: "repair_user",
      email: showPwd.email,
      password: newPwd,
      full_name: showPwd.full_name,
      role: showPwd.role,
    });
    setLoading(false);
    if (result.error) {
      setMsg("Force reset failed: " + result.error);
    } else {
      setMsg(`✅ Login fixed (${result.action}). User can log in with the new password now.`);
      setShowPwd(null);
      setNewPwd("");
      loadAll();
    }
  };

  const togglePermission = (role, module) => {
    setEditPerms(prev => {
      const current = prev[role] || [];
      const newMods = current.includes(module) ? current.filter(m => m !== module) : [...current, module];
      return { ...prev, [role]: newMods };
    });
  };

  const savePermissions = async (role) => {
    setLoading(true);
    await supabase.from("role_permissions").update({ modules: editPerms[role], updated_at: new Date().toISOString() }).eq("role", role);
    await refreshPermissions();
    setLoading(false);
    setMsg(`Permissions for ${role} updated!`);
  };

  // ── access gating ─────────────────────────────────────────────────────
  // Admins get the full unified screen. Staff who only hold the classic
  // 'providers' permission keep exactly what they had: the providers view.
  if (!isAdmin) {
    if (hasAccess && hasAccess("providers")) return <ProvidersManager />;
    return (
      <div style={{ padding:40,textAlign:"center",background:C.redL,borderRadius:12,border:`1px solid ${C.red}40`,color:C.red,fontSize:14,fontWeight:600 }}>
        🔒 Admin access required to manage the team
      </div>
    );
  }

  return (
    <div style={{ display:"flex",flexDirection:"column",gap:18 }}>
      {msg && (
        <div style={{ padding:"10px 14px",background:msg.startsWith("Error")?C.redL:C.greenL,border:`1px solid ${msg.startsWith("Error")?C.red:C.green}40`,borderRadius:8,color:msg.startsWith("Error")?C.red:C.green,fontSize:13,fontWeight:600 }}>
          {msg}
        </div>
      )}

      <div style={{ display:"flex",gap:4,background:C.light,padding:3,borderRadius:9,border:`1px solid ${C.border}`,width:"fit-content" }}>
        {[["team","👥 Team"],["permissions","⚙️ Permissions"],["payroll","💼 Pay & Schedule"]].map(([v,l]) => (
          <button key={v} onClick={() => setTab(v)} style={{ padding:"8px 16px",fontSize:13,fontWeight:tab===v?700:500,color:tab===v?"#fff":C.muted,background:tab===v?C.gold:"none",borderRadius:7,border:"none",cursor:"pointer",fontFamily:F }}>{l}</button>
        ))}
      </div>

      {tab === "team" && (
        <>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",flexWrap:"wrap",gap:10 }}>
            <div>
              <div style={{ fontSize:16,fontWeight:700,color:C.gold,fontFamily:S }}>Team Members ({users.length})</div>
              <div style={{ fontSize:12,color:C.muted }}>Accounts, roles, provider settings, services & hours — all in one place</div>
            </div>
            <button onClick={() => setShowCreate(true)} style={{ padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:"pointer",fontFamily:F }}>+ Add Team Member</button>
          </div>

          <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden" }}>
            {users.map(u => {
              const roleInfo = ROLES.find(r => r.id === u.role) || ROLES[4];
              const prov = providerOf(u.id);
              return (
                <div key={u.id} style={{ padding:"14px 18px",borderBottom:`1px solid ${C.border}` }}>
                  <div style={{ display:"flex",alignItems:"center",gap:12,flexWrap:"wrap" }}>
                    <div style={{ width:40,height:40,borderRadius:10,background:roleInfo.color+"20",border:`1px solid ${roleInfo.color}40`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:13,fontWeight:700,color:roleInfo.color,flexShrink:0 }}>{(u.full_name || u.email).charAt(0).toUpperCase()}</div>
                    <div style={{ flex:1,minWidth:180 }}>
                      <div style={{ fontSize:13,fontWeight:700,color:C.text,display:"flex",alignItems:"center",gap:8 }}>
                        {u.full_name || "No name"}
                        {prov && <span title="Bookable provider — shows on the calendar" style={{ width:10,height:10,borderRadius:"50%",background:prov.color||C.gold,display:"inline-block",border:"1px solid rgba(0,0,0,0.15)" }} />}
                      </div>
                      <div style={{ fontSize:11,color:C.muted }}>{u.email}</div>
                    </div>
                    <select value={u.role} onChange={e => handleRoleChange(u.id, e.target.value)} disabled={u.id === currentProfile.id} style={{ ...inp,width:"auto",fontSize:12,padding:"6px 10px",cursor:u.id===currentProfile.id?"not-allowed":"pointer" }}>
                      {ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
                    </select>
                    <span style={{ fontSize:10,fontWeight:700,padding:"4px 10px",borderRadius:20,background:u.status==="active"?C.greenL:C.redL,color:u.status==="active"?C.green:C.red,textTransform:"uppercase",letterSpacing:"0.05em" }}>{u.status}</span>
                    <button onClick={() => openNotifPrefs(u)} style={btn(C.blueL, C.blue, `1px solid ${C.blue}40`)}>🔔 Notifications</button>
                    <button onClick={() => setShowPwd(u)} style={btn(C.goldBg, C.gold, `1px solid ${C.borderGold}`)}>🔑 Password</button>
                    {u.id !== currentProfile.id && (
                      <>
                        <button onClick={() => handleStatusToggle(u.id, u.status)} style={btn(u.status==="active"?C.redL:C.greenL, u.status==="active"?C.red:C.green, `1px solid ${u.status==="active"?C.red:C.green}40`)}>{u.status==="active"?"Deactivate":"Activate"}</button>
                        <button onClick={() => handleDelete(u.id, u.full_name || u.email)} style={btn(C.redL, C.red, `1px solid ${C.red}40`)}>🗑️</button>
                      </>
                    )}
                  </div>
                  {/* Provider strip — the "second section" folded into the row */}
                  <div style={{ display:"flex",alignItems:"center",gap:8,marginTop:8,paddingTop:8,borderTop:`1px dashed ${C.border}`,flexWrap:"wrap" }}>
                    {prov ? (
                      <>
                        <span style={{ fontSize:11,color:C.muted }}>
                          🩺 Bookable provider · <b style={{color:C.text}}>{(providerServices[prov.id]||[]).length}</b> services{!prov.active && <span style={{color:C.red,fontWeight:700}}> · NOT ACCEPTING BOOKINGS</span>}
                        </span>
                        <div style={{ flex:1 }} />
                        <button onClick={() => openProviderEdit(prov)} style={btn(C.goldBg, C.gold, `1px solid ${C.borderGold}`)}>🩺 Provider Settings & Services</button>
                        <button onClick={() => setHoursFor(prov.id)} style={btn((prov.color||C.gold)+"15", prov.color||C.gold, `1px solid ${(prov.color||C.gold)}40`)}>📅 Hours</button>
                      </>
                    ) : (
                      <>
                        <span style={{ fontSize:11,color:C.dim,fontStyle:"italic" }}>Not bookable — has no calendar column</span>
                        <div style={{ flex:1 }} />
                        <button onClick={() => makeBookable(u)} style={btn(C.greenL, C.green, `1px solid ${C.green}40`)}>+ Make Bookable Provider</button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {orphanProviders.length > 0 && (
            <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:16 }}>
              <div style={{ fontSize:13,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4 }}>Providers without a login account ({orphanProviders.length})</div>
              <div style={{ fontSize:11,color:C.muted,marginBottom:12 }}>They show on the calendar but can't log in. Use "Provider Settings" → "Link to User Account" to connect one, or leave as display-only.</div>
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {orphanProviders.map(p => (
                  <div key={p.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 12px",background:C.light,borderRadius:8,flexWrap:"wrap" }}>
                    <span style={{ width:10,height:10,borderRadius:"50%",background:p.color||C.gold,flexShrink:0 }} />
                    <div style={{ flex:1,minWidth:140,fontSize:13,fontWeight:700,color:C.text }}>{p.name}{!p.active && <span style={{fontSize:10,color:C.red,marginLeft:8}}>INACTIVE</span>}</div>
                    <span style={{ fontSize:11,color:C.muted }}>{(providerServices[p.id]||[]).length} services</span>
                    <button onClick={() => openProviderEdit(p)} style={btn(C.goldBg, C.gold, `1px solid ${C.borderGold}`)}>🩺 Provider Settings</button>
                    <button onClick={() => setHoursFor(p.id)} style={btn((p.color||C.gold)+"15", p.color||C.gold, `1px solid ${(p.color||C.gold)}40`)}>📅 Hours</button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {tab === "payroll" && <PayrollSettings users={users} onUpdate={loadAll} />}

      {tab === "permissions" && (
        <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
          <div style={{ padding:"12px 16px",background:C.goldBg,border:`1px solid ${C.borderGold}`,borderRadius:10,fontSize:12,color:C.gold }}>
            ⚙️ Configure which modules each role can access. Admin always has full access.
          </div>
          {ROLES.filter(r => r.id !== "admin").map(role => (
            <div key={role.id} style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:20 }}>
              <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14,paddingBottom:12,borderBottom:`1px solid ${C.border}` }}>
                <div>
                  <div style={{ fontSize:15,fontWeight:700,color:role.color,fontFamily:S }}>{role.label}</div>
                  <div style={{ fontSize:11,color:C.muted }}>{role.desc}</div>
                </div>
                <button onClick={() => savePermissions(role.id)} disabled={loading} style={{ padding:"8px 16px",fontSize:12,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:7,cursor:"pointer",fontFamily:F }}>💾 Save</button>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",gap:8 }}>
                {ALL_MODULES.map(m => {
                  const checked = (editPerms[role.id] || []).includes(m);
                  return (
                    <label key={m} style={{ display:"flex",alignItems:"center",gap:8,padding:"8px 10px",background:checked?role.color+"15":C.light,border:`1px solid ${checked?role.color+"40":C.border}`,borderRadius:7,cursor:"pointer",fontSize:12 }}>
                      <input type="checkbox" checked={checked} onChange={() => togglePermission(role.id, m)} style={{ accentColor:role.color,cursor:"pointer" }} />
                      <span style={{ color:checked?role.color:C.text,fontWeight:checked?600:400 }}>{MODULE_LABELS[m] || m}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {showCreate && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }}>
          <div style={{ background:C.surface,borderRadius:16,width:"100%",maxWidth:460,padding:24,maxHeight:"92vh",overflowY:"auto" }}>
            <div style={{ fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:16 }}>Add Team Member</div>
            <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
              <div>
                <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>Full Name *</label>
                <input value={form.full_name} onChange={e => setForm({ ...form,full_name:e.target.value })} style={inp} placeholder="e.g. Esti Cohen" />
              </div>
              <div>
                <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>Email *</label>
                <input type="email" value={form.email} onChange={e => setForm({ ...form,email:e.target.value })} style={inp} placeholder="user@example.com" />
              </div>
              <div>
                <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>Password * (min 6 chars)</label>
                <input type="text" value={form.password} onChange={e => setForm({ ...form,password:e.target.value })} style={inp} placeholder="Choose a password" />
              </div>
              <div>
                <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>Role *</label>
                <select value={form.role} onChange={e => {
                  const role = e.target.value;
                  setForm({ ...form, role, bookable: BOOKABLE_BY_DEFAULT.has(role) });
                }} style={inp}>
                  {ROLES.map(r => <option key={r.id} value={r.id}>{r.label} - {r.desc}</option>)}
                </select>
              </div>
              <label style={{ display:"flex",alignItems:"center",gap:10,padding:"12px 14px",background:form.bookable?C.greenL:C.light,border:`1px solid ${form.bookable?C.green+"40":C.border}`,borderRadius:8,cursor:"pointer" }}>
                <input type="checkbox" checked={form.bookable} onChange={e => setForm({ ...form, bookable: e.target.checked })} style={{ accentColor:C.green,width:16,height:16,cursor:"pointer" }} />
                <div>
                  <div style={{ fontSize:13,fontWeight:700,color:C.text }}>🩺 Bookable provider</div>
                  <div style={{ fontSize:11,color:C.muted }}>Gets a calendar column — you'll pick services right after, then set hours</div>
                </div>
              </label>
              {form.bookable && (
                <div>
                  <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>Calendar Color</label>
                  <div style={{ display:"flex",gap:6,flexWrap:"wrap" }}>
                    {PROVIDER_COLORS.map(c => (
                      <button key={c} type="button" onClick={() => setForm({ ...form, color: c })} style={{ width:30,height:30,borderRadius:"50%",background:c,border:form.color===c?"3px solid #000":"2px solid #fff",boxShadow:`0 0 0 1px ${C.border}`,cursor:"pointer" }} />
                    ))}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display:"flex",gap:10,justifyContent:"flex-end",marginTop:20 }}>
              <button onClick={() => { setShowCreate(false); setMsg(""); }} style={{ padding:"10px 16px",fontSize:13,fontWeight:600,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",fontFamily:F }}>Cancel</button>
              <button onClick={handleCreate} disabled={loading} style={{ padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:loading?"not-allowed":"pointer",fontFamily:F,opacity:loading?0.6:1 }}>{loading?"Creating...":"Create"}</button>
            </div>
          </div>
        </div>
      )}

      {showPwd && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }}>
          <div style={{ background:C.surface,borderRadius:16,width:"100%",maxWidth:420,padding:24 }}>
            <div style={{ fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:6 }}>Change Password</div>
            <div style={{ fontSize:12,color:C.muted,marginBottom:16 }}>For: {showPwd.full_name || showPwd.email}</div>
            <div>
              <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>New Password (min 6 chars)</label>
              <input type="text" value={newPwd} onChange={e => setNewPwd(e.target.value)} style={inp} placeholder="New password" />
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"space-between",marginTop:20,flexWrap:"wrap" }}>
              <button onClick={handleForceReset} disabled={loading} title="Use this if the user can't log in even after a password reset. Repairs missing or broken auth account."
                style={{ padding:"10px 14px",fontSize:11,fontWeight:700,background:"#fff",color:C.red,border:`1px solid ${C.red}50`,borderRadius:8,cursor:loading?"not-allowed":"pointer",fontFamily:F,opacity:loading?0.6:1 }}>
                🔧 Force Reset Login
              </button>
              <div style={{display:"flex",gap:8}}>
                <button onClick={() => { setShowPwd(null); setNewPwd(""); setMsg(""); }} style={{ padding:"10px 16px",fontSize:13,fontWeight:600,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",fontFamily:F }}>Cancel</button>
                <button onClick={handlePasswordChange} disabled={loading} style={{ padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:loading?"not-allowed":"pointer",fontFamily:F,opacity:loading?0.6:1 }}>{loading?"Updating...":"Update Password"}</button>
              </div>
            </div>
            <div style={{fontSize:10,color:C.muted,marginTop:10,lineHeight:1.5}}>
              <strong>Update Password</strong> — change password for an existing working account.<br/>
              <strong>🔧 Force Reset Login</strong> — use when "Update Password" doesn't fix login. Repairs missing/broken auth records.
            </div>
          </div>
        </div>
      )}

      {showNotif && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center",padding:20,backdropFilter:"blur(4px)" }}>
          <div style={{ background:C.surface,borderRadius:16,width:"100%",maxWidth:520,padding:24,maxHeight:"92vh",overflowY:"auto" }}>
            <div style={{ fontSize:18,fontWeight:700,color:C.gold,fontFamily:S,marginBottom:4 }}>🔔 Notification Settings</div>
            <div style={{ fontSize:12,color:C.muted,marginBottom:18 }}>For: <strong style={{color:C.text}}>{showNotif.full_name || showNotif.email}</strong></div>

            <div style={{marginBottom:18}}>
              <label style={{ display:"block",fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:6 }}>📱 Phone for SMS alerts</label>
              <input type="tel" value={notifDraft.notif_phone} onChange={e => setNotifDraft(d => ({...d, notif_phone: e.target.value}))} style={inp} placeholder="+1 555 555 5555" />
              <div style={{fontSize:10,color:C.muted,marginTop:4}}>Leave blank to disable SMS only. The toggles below now ALSO control the popup alerts on this user's screen.</div>
            </div>

            <div style={{fontSize:11,fontWeight:700,color:C.gold,textTransform:"uppercase",letterSpacing:"0.08em",marginBottom:8}}>Notifications — controls SMS + in-app popups</div>

            {[
              { key: "new_leads",     label: "🆕 New Lead Arrives",        desc: "SMS + popup when a hot lead needs calling (Facebook/Booking form)" },
              { key: "new_bookings",  label: "📅 New Appointment Booked", desc: "SMS when public booking, AI bot, or staff books a slot" },
              { key: "complaints",    label: "⚠️ Complaint / AI Handoff",  desc: "SMS + popup when bot escalates or detects complaint (recommended ON)" },
              { key: "cancellations", label: "🚫 Cancellations",            desc: "SMS when a client cancels an appointment" },
              { key: "no_shows",      label: "👻 No-Show Marked",           desc: "SMS when an appointment is marked no-show" },
              { key: "verbal_confirm", label: "📞 Verbal Confirmation Calls", desc: "SMS 2 hours after Sophia (text or call) books someone: call the client now to confirm" },
            ].map(({key, label, desc}) => {
              const on = key === "verbal_confirm" ? notifDraft.notif_prefs[key] === true : notifDraft.notif_prefs[key] !== false;
              return (
                <div key={key} style={{display:"flex",alignItems:"center",gap:12,padding:"12px 14px",background:on?C.greenL:C.light,border:`1px solid ${on?C.green+"40":C.border}`,borderRadius:8,marginBottom:6,cursor:"pointer"}}
                     onClick={() => setNotifDraft(d => ({...d, notif_prefs: {...d.notif_prefs, [key]: !on}}))}>
                  <div style={{width:42,height:24,borderRadius:14,background:on?C.green:C.dim,position:"relative",transition:"all 0.2s",flexShrink:0}}>
                    <div style={{position:"absolute",top:2,left:on?20:2,width:20,height:20,borderRadius:"50%",background:"#fff",transition:"all 0.2s",boxShadow:"0 1px 3px rgba(0,0,0,0.3)"}}/>
                  </div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:C.text}}>{label}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:2}}>{desc}</div>
                  </div>
                </div>
              );
            })}

            <div style={{ display:"flex",gap:10,justifyContent:"flex-end",marginTop:20 }}>
              <button onClick={() => setShowNotif(null)} style={{ padding:"10px 16px",fontSize:13,fontWeight:600,background:C.light,color:C.muted,border:`1px solid ${C.border}`,borderRadius:8,cursor:"pointer",fontFamily:F }}>Cancel</button>
              <button onClick={saveNotifPrefs} disabled={loading} style={{ padding:"10px 20px",fontSize:13,fontWeight:700,background:`linear-gradient(135deg,${C.gold},${C.goldD})`,color:"#fff",border:"none",borderRadius:8,cursor:loading?"not-allowed":"pointer",fontFamily:F,opacity:loading?0.6:1 }}>{loading?"Saving...":"💾 Save"}</button>
            </div>
          </div>
        </div>
      )}

      {provForm && (
        <ProviderFormModal
          provider={provForm}
          setProvider={setProvForm}
          onClose={() => setProvForm(null)}
          onSave={saveProvider}
          users={users}
          services={services}
        />
      )}

      {hoursFor && <ProviderHoursModal providerId={hoursFor} onClose={() => setHoursFor(null)} />}
    </div>
  );
}
